import {
  ArrowLeft,
  Clock3,
  Download,
  FileAudio,
  ImagePlus,
  Mic,
  RefreshCcw,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Square,
  UploadCloud,
  Volume2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardFrame, { type DashboardNavItem } from '../components/DashboardFrame';
import { buyerNavItemsConfig } from '../buyerDashboardConfig';
import {
  setBuyerRecordedAudioDraft,
  type AudioCleanupLevel,
  type AudioToneProfile,
} from '../utils/buyerRecordedAudioDraft';
import { addUploadedStudioLibraryItem } from '../utils/uploadedStudioLibrary';
import { mediaRecordingService, type RecordingUploadResult } from '../../../shared/services';
import { useShop } from '../../../shared/store';

type RecorderStatus = 'idle' | 'requesting' | 'recording' | 'ready' | 'uploading' | 'success' | 'error';

const cleanupProfiles: Record<AudioCleanupLevel, { label: string; highpass: number; presenceGain: number }> = {
  gentle: { label: 'Gentle cleanup', highpass: 70, presenceGain: 0.7 },
  balanced: { label: 'Balanced cleanup', highpass: 90, presenceGain: 1.6 },
  clear: { label: 'Clear voice', highpass: 120, presenceGain: 2.8 },
};

const toneProfiles: Record<AudioToneProfile, { label: string; filterType: BiquadFilterType; frequency: number; gain: number }> = {
  natural: { label: 'Natural', filterType: 'peaking', frequency: 1000, gain: 0 },
  warm: { label: 'Warm', filterType: 'lowshelf', frequency: 220, gain: 3.2 },
  bright: { label: 'Bright', filterType: 'highshelf', frequency: 2600, gain: 3.4 },
};

const defaultRecorderName = () => `haapa-audio-${new Date().toISOString().replace(/[:.]/g, '-')}`;

const sanitizeFileBaseName = (value: string, fallback: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || fallback;

const pickAudioMimeType = () => {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined;
  }

  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg'].find((candidate) =>
    MediaRecorder.isTypeSupported(candidate),
  );
};

const extensionFromMimeType = (mimeType: string) => {
  if (mimeType.includes('ogg')) {
    return 'ogg';
  }

  if (mimeType.includes('wav')) {
    return 'wav';
  }

  if (mimeType.includes('mpeg') || mimeType.includes('mp3')) {
    return 'mp3';
  }

  return 'webm';
};

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const secs = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${mins}:${secs}`;
};

const formatFileSize = (value?: number) => {
  if (!value) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const normalizedSize = value / 1024 ** unitIndex;
  return `${normalizedSize.toFixed(normalizedSize >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const readFileAsDataUrl = async (file: File | null): Promise<string | undefined> => {
  if (!file) {
    return undefined;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : undefined);
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read selected image.'));
    reader.readAsDataURL(file);
  });
};

const BuyerAudioRecorderPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useShop();
  const navigate = useNavigate();
  const navItems = useMemo<DashboardNavItem[]>(
    () =>
      buyerNavItemsConfig.map((item) => ({
        ...item,
        label: item.label ?? (item.labelKey ? t(item.labelKey) : item.id),
      })),
    [t],
  );
  const supportsRecording =
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    typeof window.navigator.mediaDevices?.getUserMedia === 'function';

  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [feedback, setFeedback] = useState('Open your microphone, capture a clean voice take, and send it back into Create Content.');
  const [recordingName, setRecordingName] = useState(defaultRecorderName);
  const [noiseCancellation, setNoiseCancellation] = useState(true);
  const [echoCancellation, setEchoCancellation] = useState(true);
  const [autoGain, setAutoGain] = useState(true);
  const [cleanupLevel, setCleanupLevel] = useState<AudioCleanupLevel>('balanced');
  const [toneProfile, setToneProfile] = useState<AudioToneProfile>('natural');
  const [previewVolume, setPreviewVolume] = useState(90);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [recordedFile, setRecordedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<RecordingUploadResult | null>(null);
  const [captureSource, setCaptureSource] = useState<'microphone' | 'upload'>('microphone');
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const sourceStreamRef = useRef<MediaStream | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const revokePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  const setNextPreviewUrl = (nextUrl: string | null) => {
    revokePreviewUrl();
    previewUrlRef.current = nextUrl;
    setPreviewUrl(nextUrl);
  };

  const stopTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopVisualizer = () => {
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const stopAudioSession = () => {
    sourceStreamRef.current?.getTracks().forEach((track) => track.stop());
    sourceStreamRef.current = null;
    if (audioContextRef.current) {
      void audioContextRef.current.close();
      audioContextRef.current = null;
    }
    stopTimer();
    stopVisualizer();
  };

  const drawIdleWaveform = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (!canvas || !context) {
      return;
    }

    context.clearRect(0, 0, canvas.width, canvas.height);
    const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#fff7ed');
    gradient.addColorStop(1, '#ffedd5');
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);

    for (let index = 0; index < 48; index += 1) {
      const x = 18 + index * 18;
      const height = 30 + ((Math.sin(index * 0.5) + 1) / 2) * 120;
      context.fillStyle = index % 3 === 0 ? '#ea580c' : index % 2 === 0 ? '#fb923c' : '#fdba74';
      context.fillRect(x, canvas.height / 2 - height / 2, 8, height);
    }
  };

  const startVisualizer = (analyser: AnalyserNode) => {
    stopVisualizer();
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (!canvas || !context) {
      return;
    }

    const values = new Uint8Array(analyser.frequencyBinCount);
    const draw = () => {
      analyser.getByteFrequencyData(values);
      context.clearRect(0, 0, canvas.width, canvas.height);

      const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#fff7ed');
      gradient.addColorStop(1, '#ffffff');
      context.fillStyle = gradient;
      context.fillRect(0, 0, canvas.width, canvas.height);

      values.forEach((value, index) => {
        const width = 8;
        const gap = 8;
        const x = 18 + index * (width + gap);
        const height = Math.max(16, (value / 255) * (canvas.height - 36));
        context.fillStyle = value > 180 ? '#c2410c' : value > 100 ? '#f97316' : '#fdba74';
        context.fillRect(x, canvas.height / 2 - height / 2, width, height);
      });

      animationFrameRef.current = window.requestAnimationFrame(draw);
    };

    draw();
  };

  const startTimer = () => {
    stopTimer();
    setElapsedSeconds(0);
    timerRef.current = window.setInterval(() => {
      setElapsedSeconds((current) => current + 1);
    }, 1000);
  };

  const resetTake = (message = 'Recorder reset. Start a fresh take or choose an existing audio file.') => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    stopAudioSession();
    setRecordedFile(null);
    setElapsedSeconds(0);
    setNextPreviewUrl(null);
    setUploadResult(null);
    setCoverImageFile(null);
    setStatus('idle');
    setFeedback(message);
    drawIdleWaveform();
  };

  const createProcessedStream = async (stream: MediaStream) => {
    if (typeof window.AudioContext === 'undefined') {
      drawIdleWaveform();
      return stream;
    }

    const audioContext = new window.AudioContext();
    await audioContext.resume().catch(() => undefined);

    const source = audioContext.createMediaStreamSource(stream);
    let currentNode: AudioNode = source;

    const cleanup = cleanupProfiles[cleanupLevel];
    const highpass = audioContext.createBiquadFilter();
    highpass.type = 'highpass';
    highpass.frequency.value = cleanup.highpass;
    currentNode.connect(highpass);
    currentNode = highpass;

    const clarity = audioContext.createBiquadFilter();
    clarity.type = 'peaking';
    clarity.frequency.value = 3200;
    clarity.Q.value = 1.2;
    clarity.gain.value = cleanup.presenceGain;
    currentNode.connect(clarity);
    currentNode = clarity;

    const tone = toneProfiles[toneProfile];
    if (tone.gain !== 0) {
      const toneFilter = audioContext.createBiquadFilter();
      toneFilter.type = tone.filterType;
      toneFilter.frequency.value = tone.frequency;
      toneFilter.gain.value = tone.gain;
      currentNode.connect(toneFilter);
      currentNode = toneFilter;
    }

    const compressor = audioContext.createDynamicsCompressor();
    compressor.threshold.value = -24;
    compressor.knee.value = 24;
    compressor.ratio.value = 3;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.2;
    currentNode.connect(compressor);
    currentNode = compressor;

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.82;
    currentNode.connect(analyser);
    startVisualizer(analyser);

    const destination = audioContext.createMediaStreamDestination();
    currentNode.connect(destination);

    audioContextRef.current = audioContext;
    return destination.stream;
  };

  const prepareNamedFile = (file: File) => {
    const extension = file.name.split('.').pop() ?? extensionFromMimeType(file.type || 'audio/webm');
    return new File([file], `${sanitizeFileBaseName(recordingName, 'haapa-audio')}.${extension}`, {
      type: file.type || 'audio/webm',
    });
  };

  const handlePickedFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    stopAudioSession();
    recorderRef.current = null;
    setRecordedFile(file);
    setRecordingName(file.name.replace(/\.[^.]+$/, '') || defaultRecorderName());
    setStatus('ready');
    setFeedback('Audio file added. You can preview it, rename it, and use it in your content draft.');
    setCaptureSource('upload');
    setUploadResult(null);
    setNextPreviewUrl(URL.createObjectURL(file));
  };

  const handleStartRecording = async () => {
    if (!supportsRecording) {
      setStatus('error');
      setFeedback('This browser does not support in-app microphone recording.');
      return;
    }

    resetTake('Requesting microphone access...');
    setStatus('requesting');

    try {
      const stream = await window.navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          noiseSuppression: noiseCancellation,
          echoCancellation,
          autoGainControl: autoGain,
        },
        video: false,
      });
      sourceStreamRef.current = stream;

      const processedStream = await createProcessedStream(stream);
      const mimeType = pickAudioMimeType();
      const recorder = mimeType ? new MediaRecorder(processedStream, { mimeType }) : new MediaRecorder(processedStream);
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const finalMimeType = recorder.mimeType || mimeType || 'audio/webm';
        const nextFile = new File(
          [new Blob(chunksRef.current, { type: finalMimeType })],
          `${sanitizeFileBaseName(recordingName, 'haapa-audio')}.${extensionFromMimeType(finalMimeType)}`,
          { type: finalMimeType },
        );

        setRecordedFile(nextFile);
        setNextPreviewUrl(URL.createObjectURL(nextFile));
        setStatus('ready');
        setFeedback('Recording ready. Preview it and send it back to Create Content when you are happy.');
        setCaptureSource('microphone');
        setUploadResult(null);
        stopAudioSession();
        drawIdleWaveform();
      };

      recorder.start(250);
      startTimer();
      setStatus('recording');
      setFeedback('Recording in progress. Speak clearly into your microphone.');
    } catch (error: unknown) {
      stopAudioSession();
      setStatus('error');
      setFeedback(error instanceof Error ? error.message : 'Unable to start microphone recording.');
      drawIdleWaveform();
    }
  };

  const handleStopRecording = () => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
  };

  const handleUseInDraft = () => {
    if (!recordedFile) {
      setStatus('error');
      setFeedback('Record a take or choose an audio file first.');
      return;
    }

    const preparedFile = prepareNamedFile(recordedFile);
    setBuyerRecordedAudioDraft({
      file: preparedFile,
      imageFile: coverImageFile,
      createdAt: Date.now(),
      source: 'recorder',
      noiseCancellation,
      echoCancellation,
      autoGain,
      cleanupLevel,
      toneProfile,
    });
    void navigate('/dashboard/create-content');
  };

  const handleCoverImagePicked = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setCoverImageFile(file);
    if (file) {
      setFeedback(`Cover image "${file.name}" added. You can upload audio or use the draft now.`);
    }
  };

  const handleUpload = async () => {
    if (!recordedFile) {
      setStatus('error');
      setFeedback('Record a take or choose an audio file before uploading.');
      return;
    }

    const preparedFile = prepareNamedFile(recordedFile);

    try {
      setStatus('uploading');
      setFeedback('Uploading audio to POST /api/media/recordings/audio ...');
      const result = await mediaRecordingService.uploadRecording({
        endpoint: 'audio',
        file: preparedFile,
        sourceType: captureSource,
        fallbackUserId: currentUser?.id,
        title: recordingName || preparedFile.name,
        previewImageFile: coverImageFile,
      });
      const imageUrl = await readFileAsDataUrl(coverImageFile).catch(() => undefined);
      setRecordedFile(preparedFile);
      setUploadResult(result);
      addUploadedStudioLibraryItem({
        id: result.recordingId ? `recording-audio-${result.recordingId}` : result.publicId ?? `${preparedFile.name}-${Date.now()}`,
        userId: currentUser?.id,
        title: recordingName || preparedFile.name,
        mediaType: 'audio',
        mediaUrl: result.url || previewUrl || '',
        imageUrl: result.previewImageUrl ?? imageUrl ?? '/vite.svg',
        uploadedAt: new Date().toISOString(),
        backendUrl: result.url,
      });
      setStatus('success');
      setFeedback('Audio uploaded successfully, saved in the backend database, and added to the public audio feed.');
    } catch (error: unknown) {
      setStatus('error');
      setFeedback(error instanceof Error ? error.message : 'Audio upload failed.');
    }
  };

  const handleDownload = () => {
    if (!recordedFile) {
      setStatus('error');
      setFeedback('There is no audio take to download yet.');
      return;
    }

    const preparedFile = prepareNamedFile(recordedFile);
    const downloadUrl = URL.createObjectURL(preparedFile);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = preparedFile.name;
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
  };

  useEffect(() => {
    drawIdleWaveform();

    return () => {
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.stop();
      }
      sourceStreamRef.current?.getTracks().forEach((track) => track.stop());
      sourceStreamRef.current = null;
      if (audioContextRef.current) {
        void audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!previewAudioRef.current) {
      return;
    }

    previewAudioRef.current.volume = previewVolume / 100;
    previewAudioRef.current.playbackRate = playbackSpeed;
  }, [playbackSpeed, previewVolume, previewUrl]);

  const statusTone =
    status === 'recording'
      ? 'bg-red-100 text-red-700'
      : status === 'uploading'
        ? 'bg-amber-100 text-amber-700'
        : status === 'success'
          ? 'bg-emerald-100 text-emerald-700'
      : status === 'ready'
        ? 'bg-emerald-100 text-emerald-700'
        : status === 'error'
          ? 'bg-red-100 text-red-700'
          : 'bg-orange-100 text-orange-700';

  const recordingSummary = `${cleanupProfiles[cleanupLevel].label}, ${toneProfiles[toneProfile].label} tone`;

  return (
    <DashboardFrame
      brand={t('dashboardBuyer.brand')}
      roleLabel={t('dashboardBuyer.roleLabel')}
      userName={currentUser?.name ?? t('dashboardBuyer.defaultUserName')}
      userImageUrl={currentUser?.profileImageUrl}
      pageTitle="Audio Studio"
      pageDescription="Record your voice in a dedicated microphone workspace and send the result back into Create Content."
      navItems={navItems}
      activeNavId="create-content"
      headerVariant="buyer"
      showSettings={false}
      headerAction={
        <div className="flex flex-wrap gap-2">
          <Link
            to="/dashboard/create-content"
            className="inline-flex items-center gap-2 rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-orange-700 transition hover:bg-orange-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Back To Draft
          </Link>
          <Link
            to="/products"
            className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
          >
            {t('dashboardBuyer.actions.browseBooks')}
          </Link>
        </div>
      }
    >
      <div className="space-y-6">
        <section className="overflow-hidden rounded-[30px] border border-orange-200 bg-[radial-gradient(circle_at_top_left,_rgba(251,146,60,0.22),_transparent_30%),linear-gradient(135deg,_#fff7ed,_#ffffff_55%,_#ffedd5)] p-6 shadow-[0_30px_90px_-60px_rgba(249,115,22,0.75)]">
          <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-orange-600">Haapa Creator Studio</p>
              <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                Professional voice recording with direct backend publishing
              </h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                Capture clean narration, shape the sound, upload it to your audio endpoint, then attach the same take to your content draft without leaving the studio.
              </p>
              <div className="mt-5 flex flex-wrap gap-3 text-xs font-semibold">
                <span className="rounded-full border border-orange-200 bg-white/90 px-3 py-1.5 text-orange-700">Noise cleanup</span>
                <span className="rounded-full border border-orange-200 bg-white/90 px-3 py-1.5 text-orange-700">Live preview</span>
                <span className="rounded-full border border-orange-200 bg-white/90 px-3 py-1.5 text-orange-700">API upload</span>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
              <div className="rounded-3xl border border-white/80 bg-white/85 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Endpoint</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">POST `/api/media/recordings/audio`</p>
                <p className="mt-1 text-xs text-slate-500">Maps to `http://localhost:4000/api/media/recordings/audio` in local development.</p>
              </div>
              <div className="rounded-3xl border border-white/80 bg-white/85 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Source</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{captureSource === 'microphone' ? 'Microphone capture' : 'Uploaded audio file'}</p>
              </div>
              <div className="rounded-3xl border border-white/80 bg-white/85 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Upload Status</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{uploadResult?.url ? 'Uploaded' : 'Not uploaded yet'}</p>
              </div>
            </div>
          </div>
        </section>
        {status === 'success' && uploadResult ? (
          <section className="rounded-[26px] border border-emerald-200 bg-emerald-50 px-5 py-4 shadow-[0_20px_45px_-35px_rgba(16,185,129,0.75)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Upload Complete</p>
                <p className="mt-1 text-base font-semibold text-slate-900">Audio uploaded successfully.</p>
                <p className="mt-1 text-sm text-slate-600">Your recording is now saved on the backend and is still ready to attach to Create Content.</p>
              </div>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-emerald-700">Success</span>
            </div>
          </section>
        ) : null}
        <section className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="space-y-4">
            <article className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl bg-orange-500 text-white shadow-[0_20px_45px_-28px_rgba(249,115,22,0.85)]">
                  <Mic className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Voice Capture</p>
                  <h2 className="text-lg font-semibold text-slate-900">Recording Setup</h2>
                </div>
              </div>

              <div className="mt-5 space-y-3 text-sm">
                <label className="grid gap-2">
                  <span className="font-semibold text-slate-700">Recording name</span>
                  <input
                    value={recordingName}
                    onChange={(event) => setRecordingName(event.target.value)}
                    className="rounded-2xl border border-orange-200 px-4 py-3 text-slate-800 focus:border-orange-400 focus:outline-none"
                  />
                </label>

                <div className="rounded-2xl border border-orange-100 bg-orange-50/70 p-4">
                  <div className="flex items-center gap-2 text-slate-800">
                    <ShieldCheck className="h-4 w-4 text-orange-600" />
                    <span className="font-semibold">Capture cleanup</span>
                  </div>
                  <div className="mt-3 space-y-3">
                    <label className="flex items-center justify-between gap-3">
                      <span className="text-slate-600">Noise cancellation</span>
                      <input type="checkbox" checked={noiseCancellation} onChange={(event) => setNoiseCancellation(event.target.checked)} className="h-4 w-4 accent-orange-500" />
                    </label>
                    <label className="flex items-center justify-between gap-3">
                      <span className="text-slate-600">Echo cancellation</span>
                      <input type="checkbox" checked={echoCancellation} onChange={(event) => setEchoCancellation(event.target.checked)} className="h-4 w-4 accent-orange-500" />
                    </label>
                    <label className="flex items-center justify-between gap-3">
                      <span className="text-slate-600">Auto gain</span>
                      <input type="checkbox" checked={autoGain} onChange={(event) => setAutoGain(event.target.checked)} className="h-4 w-4 accent-orange-500" />
                    </label>
                  </div>
                </div>
              </div>
            </article>

            <article className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 text-orange-600" />
                <h3 className="text-base font-semibold text-slate-900">Basic audio polish</h3>
              </div>
              <div className="mt-4 space-y-4 text-sm">
                <label className="grid gap-2">
                  <span className="font-semibold text-slate-700">Cleanup profile</span>
                  <select
                    value={cleanupLevel}
                    onChange={(event) => setCleanupLevel(event.target.value as AudioCleanupLevel)}
                    className="rounded-2xl border border-orange-200 bg-white px-4 py-3 text-slate-800 focus:border-orange-400 focus:outline-none"
                  >
                    {Object.entries(cleanupProfiles).map(([value, option]) => (
                      <option key={value} value={value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="grid gap-2">
                  <span className="font-semibold text-slate-700">Tone shape</span>
                  <select
                    value={toneProfile}
                    onChange={(event) => setToneProfile(event.target.value as AudioToneProfile)}
                    className="rounded-2xl border border-orange-200 bg-white px-4 py-3 text-slate-800 focus:border-orange-400 focus:outline-none"
                  >
                    {Object.entries(toneProfiles).map(([value, option]) => (
                      <option key={value} value={value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="grid gap-2">
                  <span className="flex items-center justify-between font-semibold text-slate-700">
                    <span>Preview volume</span>
                    <span className="text-xs text-slate-500">{previewVolume}%</span>
                  </span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={previewVolume}
                    onChange={(event) => setPreviewVolume(Number(event.target.value))}
                    className="accent-orange-500"
                  />
                </label>

                <label className="grid gap-2">
                  <span className="flex items-center justify-between font-semibold text-slate-700">
                    <span>Playback speed</span>
                    <span className="text-xs text-slate-500">{playbackSpeed.toFixed(2)}x</span>
                  </span>
                  <input
                    type="range"
                    min="0.75"
                    max="1.5"
                    step="0.05"
                    value={playbackSpeed}
                    onChange={(event) => setPlaybackSpeed(Number(event.target.value))}
                    className="accent-orange-500"
                  />
                </label>
              </div>
            </article>
          </aside>

          <div className="space-y-4">
            <article className="overflow-hidden rounded-[30px] border border-orange-100 bg-white shadow-[0_34px_90px_-55px_rgba(15,23,42,0.42)]">
              <div className="border-b border-orange-100 bg-gradient-to-r from-orange-50 via-white to-orange-50 px-6 py-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Microphone Studio</p>
                    <h2 className="mt-1 text-2xl font-semibold text-slate-900">Record yourself with simple cleanup tools</h2>
                    <p className="mt-2 max-w-3xl text-sm text-slate-600">{feedback}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusTone}`}>{status}</span>
                    <span className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
                      <Clock3 className="h-3.5 w-3.5" />
                      {formatDuration(elapsedSeconds)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-5 p-6">
                <div className="rounded-[28px] border border-orange-100 bg-[linear-gradient(135deg,_rgba(255,247,237,1),_rgba(255,255,255,1)_45%,_rgba(255,237,213,0.9))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Sparkles className="h-4 w-4 text-orange-600" />
                      <span>{recordingSummary}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Volume2 className="h-4 w-4 text-orange-500" />
                      <span>{noiseCancellation ? 'Noise cancellation on' : 'Noise cancellation off'}</span>
                    </div>
                  </div>

                  <canvas ref={canvasRef} width={1120} height={280} className="mt-4 h-64 w-full rounded-[24px] border border-orange-100 bg-white shadow-[inset_0_1px_10px_rgba(249,115,22,0.08)]" />
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleStartRecording}
                    disabled={status === 'recording'}
                    className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Mic className="h-4 w-4" />
                    Start Recording
                  </button>
                  <button
                    type="button"
                    onClick={handleStopRecording}
                    disabled={status !== 'recording'}
                    className="inline-flex items-center gap-2 rounded-2xl border border-orange-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Square className="h-4 w-4" />
                    Stop
                  </button>
                  <button
                    type="button"
                    onClick={() => resetTake()}
                    className="inline-flex items-center gap-2 rounded-2xl border border-orange-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-orange-50"
                  >
                    <RefreshCcw className="h-4 w-4" />
                    Reset
                  </button>
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-orange-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-orange-50">
                    <UploadCloud className="h-4 w-4" />
                    Choose Audio
                    <input type="file" accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm" onChange={handlePickedFile} className="hidden" />
                  </label>
                </div>

                {previewUrl ? (
                  <div className="rounded-3xl border border-orange-100 bg-orange-50/50 p-4">
                    <p className="text-sm font-semibold text-slate-900">Preview take</p>
                    <audio ref={previewAudioRef} controls src={previewUrl} className="mt-3 w-full" />
                  </div>
                ) : null}
              </div>
            </article>

            <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
              <article className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <FileAudio className="h-4 w-4 text-orange-600" />
                  <h3 className="text-base font-semibold text-slate-900">Current take</h3>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">File name</p>
                    <p className="mt-2 text-sm font-medium text-slate-800">{recordedFile?.name ?? 'No audio selected yet'}</p>
                  </div>
                  <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Size</p>
                    <p className="mt-2 text-sm font-medium text-slate-800">{formatFileSize(recordedFile?.size)}</p>
                  </div>
                  <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Cleanup</p>
                    <p className="mt-2 text-sm font-medium text-slate-800">{cleanupProfiles[cleanupLevel].label}</p>
                  </div>
                  <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Tone</p>
                    <p className="mt-2 text-sm font-medium text-slate-800">{toneProfiles[toneProfile].label}</p>
                  </div>
                  <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4 sm:col-span-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-orange-600">Cover image</p>
                    <p className="mt-2 text-sm font-medium text-slate-800">{coverImageFile?.name ?? 'No image selected yet'}</p>
                  </div>
                </div>
                {!supportsRecording ? (
                  <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    This browser does not expose the microphone APIs needed for in-app recording. You can still choose an existing audio file.
                  </p>
                ) : null}
              </article>

              <article className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
                <h3 className="text-base font-semibold text-slate-900">Publish And Export</h3>
                <p className="mt-2 text-sm text-slate-600">
                  Upload to your backend first, or send the take straight into your draft and keep editing there.
                </p>
                <div className="mt-5 space-y-3">
                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-orange-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-orange-50">
                    <ImagePlus className="h-4 w-4" />
                    {coverImageFile ? 'Change Cover Image' : 'Add Cover Image'}
                    <input type="file" accept="image/*" onChange={handleCoverImagePicked} className="hidden" />
                  </label>
                  <button
                    type="button"
                    onClick={handleUpload}
                    disabled={!recordedFile || status === 'recording' || status === 'uploading'}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <UploadCloud className="h-4 w-4" />
                    {status === 'uploading' ? 'Uploading Audio...' : 'Upload To Audio API'}
                  </button>
                  <button
                    type="button"
                    onClick={handleUseInDraft}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
                  >
                    <UploadCloud className="h-4 w-4" />
                    Use In Create Content
                  </button>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-orange-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-orange-50"
                  >
                    <Download className="h-4 w-4" />
                    Download Audio
                  </button>
                  {uploadResult ? (
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-xs text-slate-700">
                      <p><span className="font-semibold text-slate-900">Uploaded file:</span> {uploadResult.originalName || recordedFile?.name}</p>
                      <p className="mt-1"><span className="font-semibold text-slate-900">Endpoint:</span> POST /api/media/recordings/audio</p>
                      <p className="mt-1"><span className="font-semibold text-slate-900">Size:</span> {formatFileSize(uploadResult.size ?? recordedFile?.size)}</p>
                      {uploadResult.url ? <a href={uploadResult.url} target="_blank" rel="noreferrer" className="mt-2 block break-all font-medium text-emerald-700 underline">{uploadResult.url}</a> : null}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-orange-100 bg-orange-50/70 p-4 text-xs text-slate-600">
                      <p className="font-semibold text-slate-900">Backend request</p>
                      <p className="mt-2 font-mono text-[11px] text-orange-700">curl -X POST http://localhost:4000/api/media/recordings/audio</p>
                    </div>
                  )}
                </div>
              </article>
            </div>
          </div>
        </section>
      </div>
    </DashboardFrame>
  );
};

export default BuyerAudioRecorderPage;
